import { Box, Paper, Stack, Typography } from "@mui/material";
import { StoryMediaThumbnail } from "Components/Stories/Management/StoryMediaThumbnail";
import { StoryStatusChip } from "Components/Stories/Management/StoryStatusChip";
import { type StoryListItemDto, StoryState } from "Infrastructure/Api/Api";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";

const RESOURCES = Resources.Stories.Management;

type Props = {
  story: StoryListItemDto;
  isAuthorVisible: boolean;
  /** Page-specific action buttons — the card is deliberately blind to which page renders it. */
  actions: ReactNode;
  onMediaClick: () => void;
};

export const StoryCard = ({
  story,
  isAuthorVisible,
  actions,
  onMediaClick,
}: Props) => {
  const { t } = useResource();

  return (
    <Paper
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box position="relative">
        <StoryMediaThumbnail
          story={story}
          aspectRatio="3 / 4"
          onClick={onMediaClick}
        />
        <Box position="absolute" top={8} left={8}>
          <StoryStatusChip state={story.state} />
        </Box>
      </Box>

      <Stack gap={0.5} p={1.5} flexGrow={1}>
        {isAuthorVisible && (
          <Typography variant="subtitle2" noWrap>
            {story.authorName}
          </Typography>
        )}
        {story.text && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {story.text}
          </Typography>
        )}
        <Box flexGrow={1} />
        {story.state === StoryState.Live && story.expiresAt && (
          <Typography variant="caption" color="text.secondary">
            {t(RESOURCES.Table.LiveUntil, {
              dateTime: formatDate(story.expiresAt, true),
            })}
          </Typography>
        )}
        {actions}
      </Stack>
    </Paper>
  );
};
